import React, { useEffect, useRef, useState } from "react";

// Lightweight scroll-reveal wrapper. Respects reduced-motion via CSS.
export function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`transition-all duration-700 ease-out will-change-transform ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className = "", light = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 border-l-2 pl-2.5 py-0.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] rounded-none cursor-default select-none ${
        light ? "border-cyan-600 text-cyan-700" : "border-primary text-primary"
      } ${className}`}
    >
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, align = "left", className = "", light = false }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-3xl ${alignCls} ${className}`}>
      {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-5 text-3xl sm:text-4xl font-bold text-balance leading-[1.15] ${light ? "text-slate-900" : "text-foreground"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-slate-600" : "text-muted-foreground"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
